/* eslint-disable no-unused-vars */
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import get from 'lodash/get';
import { useResizingHeight, easeOut } from '@atlaskit/motion';
import { Box, Stack, Text } from '@atlaskit/primitives';
import { Animation } from '../../constants';
import { SUMMARY_ITEM_HIDDEN_CLASS_NAME } from './summary-panel-row';
import { getSummaryPanelIcon } from './summary-panel-icon';
import './summary-panel.scss';

interface SummaryPanelContainerProps {
  title?: string;
  children?: ReactNode;
  iconKey?: string;
  isHeaderContentHidden?: boolean;
  isViewMoreItemsVisibleByDefault?: boolean;
  version?: number;
  elemAfter?: ReactNode;
}

export const getAnimationDuration = () => Animation.DEFAULT_DURATION;
export const getAnimationTimingFunction = () => easeOut;

export const SummaryPanelContainer = ({
  title,
  iconKey,
  children,
  isHeaderContentHidden = false,
  isViewMoreItemsVisibleByDefault = false,
  version,
  elemAfter,
}: SummaryPanelContainerProps) => {
  const [isViewMoreItemsVisible, setIsViewMoreItemsVisible] = useState(isViewMoreItemsVisibleByDefault);
  const [numberOfHiddenItems, setNumberOfHiddenItems] = useState(0);

  const ElementRef = useRef(null);

  useEffect(() => {
    // For new design version, we don't need to calculate hidden items
    if (version === 2) return;

    const hiddenItemCount = [...get(ElementRef, 'current.childNodes', [])].reduce(
      (accumulator: number, currentNode: ReactNode) => {
        if (get(currentNode, 'className', '').endsWith(SUMMARY_ITEM_HIDDEN_CLASS_NAME)) {
          accumulator += 1;
        }
        return accumulator;
      },
      0
    );
    setNumberOfHiddenItems(hiddenItemCount);
  }, [version]);

  if (version === 2) {
    return (
      <Stack testId="summary-container-v2">
        {title && (
          <Box paddingBlockStart="space.200" paddingBlockEnd="space.100">
            <Text weight="semibold" size="large">
              {title}
            </Text>
            {elemAfter}
          </Box>
        )}
        {children}
      </Stack>
    );
  }

  return (
    <div className="expandable-summary-container">
      {!isHeaderContentHidden && (
        <div className="expandable-summary-header">
          {iconKey && <img src={getSummaryPanelIcon(iconKey)} className="header-icon" />}
          <div className="header-text">{title}</div>
        </div>
      )}
      {/* eslint-disable-next-line react-hooks/rules-of-hooks */}
      <div {...useResizingHeight({ duration: getAnimationDuration, timingFunction: getAnimationTimingFunction })}>
        <div className={`summary ${isViewMoreItemsVisible ? 'summary-with-hidden-items' : ''}`} ref={ElementRef}>
          {children}
        </div>
      </div>
      {numberOfHiddenItems > 0 && (
        <div
          className="view-button"
          onClick={() => setIsViewMoreItemsVisible(!isViewMoreItemsVisible)}
          data-testid="view-more-section"
        >
          <div className="view-count" data-testid="view-item-count">
            {isViewMoreItemsVisible ? '-' : `+${numberOfHiddenItems}`}
          </div>
          <div className="view-text" data-testid="view-item-text">
            {isViewMoreItemsVisible ? 'View less' : 'View more'}
          </div>
        </div>
      )}
    </div>
  );
};
